import { Product } from "@/types/product";
import { App, Button, Flex, Input, TableColumnsType, Tooltip, Modal, Tree } from "antd";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import type { TreeDataNode, TreeProps } from 'antd';
import { productList, ProductListParams, categroyList } from "@/apis/product";
import { useTranslation } from "react-i18next";
// import AddOrEdit from "./AddOrEdit";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, ITableLayout, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { CustomTabPaneContext } from "@/components/TabPane";
import useCategories from "../hooks/useCategories";

import iconImage from "@/assets/images/fileIcon/image.svg";
import iconFolder from "@/assets/images/fileIcon/folder.svg";

import SText from "@/styles/SText";
import { flushSync } from "react-dom";
import _ from "lodash";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
interface ProductListProps {
  hiddenColumns?: Array<keyof Product>;
  categoryId?: string;
  selectType?: 'multi' | 'select'
}

interface QuotationProductFilters {
  keyword: string;
}

const INITIAL_QUOTATION_PRODUCT_FILTERS: QuotationProductFilters = {
  keyword: "",
};

const ProductList:FC<ProductListProps> = ({
  hiddenColumns = [],
  categoryId,
  selectType
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { onOpen } = useContext(CustomTabPaneContext);

  const tableRef = useRef<TableLayoutRef | null>(null);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<QuotationProductFilters>(
    INITIAL_QUOTATION_PRODUCT_FILTERS,
  );
  const { keyword } = draftFilters;
  const [isOpenAdd, setIsOpenAdd] = useState<boolean>(false);
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<Product | null>(null);
  const [selectProIds, setSelectProIds] = useState<Array<string>>([]);
  const [selectPros, setSelectPros] = useState<Array<Product>>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const [list, total, loading, { fetchList, addItem, updateItem, rmItem }] = useDataList<Product, [ProductListParams]>({ fetchApi: categroyList });
  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_QUOTATION_PRODUCT_FILTERS);
    fetchProducts(0, nextFilters);
    tableRef.current?.resetTablePage();
    // 切换分类时重置筛选，不能跟随每次 render 生成的 fetchProducts 重复请求。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, replaceFilters]);
  
  const columns: TableColumnsType<Product> = [
    {
      width: 100,
      title: t("product.code"),
      dataIndex: ["code"],
      hidden: hiddenColumns.includes('code'),
      sorter: (a, b) => {
        const valA = a?.code ?? "";
        const valB = b?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.code} />
    },
    {
      width: 120,
      title: t("product.name"),
      dataIndex: "productName",
      hidden: hiddenColumns.includes('name'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.name} />
    },
    {
      width: 140,
      title: t("product.brand1"),
      dataIndex: "productModel1",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.model} />
    },
    {
      width: 140,
      title: t("product.brand2"),
      dataIndex: "productModel2",
      hidden: hiddenColumns.includes('t1'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.t1 ?? ''} />
    },
    {
      width: 100,
      title: t("product.uom"),
      dataIndex: "productUom",
      hidden: hiddenColumns.includes('t1'),
      render: (_, product) => <SText text={product?.uom} />,
    },
    {
      width: 100,
      title: `${t('category.hsCode')}`,
      dataIndex: "hsCode",
      hidden: hiddenColumns.includes('hsCode'),
      sorter: (a, b) => {
        const valA = a?.hsCode ?? "";
        const valB = b?.hsCode ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, product) => <SText text={product?.hsCode || ''} />,
    },
    {
      width: 100,
      title: `cbm(${t('product.cbm')})`,
      dataIndex: "productCbm",
      hidden: hiddenColumns.includes('cbm'),
      render: (_, product) => <SText text={product?.cbm || ''} />,
    },
    {
      width: 100,
      title: `Process(${t('product.process')})`,
      dataIndex: "productProcess",
      hidden: hiddenColumns.includes('process'),
      render: (_, product) => <SText text={product?.process || ''} />,
    },
    {
      width: 100,
      dataIndex: "category",
      title: t("product.category"),
      hidden: hiddenColumns.includes('category'),
      render: (_, { category }) =>  <SText text={category?.name || ''} />,
    },
    {
      title: t('common.desc'),
      dataIndex: "productDesc",
      width: 100,
      hidden: hiddenColumns.includes('desc'),
      render: (_, { desc }) => <SText text={desc} />
    },
    {
      width: 80,
      dataIndex: "gallery",
      title: t("product.gallery"),
      hidden: hiddenColumns.includes('picDocId'),
      fixed: "right",
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconImage}
            alt=""
            onClick={() => {
              if (item.picDocId) {
                onOpen("img", item);
              } else {
                notification.error({ placement: 'top', message: t("product.dataError") });
              }
            }}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      width: 80,
      dataIndex: "folder",
      title: t("product.folder"),
      fixed: "right",
      hidden: hiddenColumns.includes('docId'),
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => {
              if (item.docId) {
                onOpen("doc", item);
              } else {
                notification.error({ placement: 'top', message: t("product.dataError") });
              }
            }}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    // {
    //   width: 100,
    //   dataIndex: "Action",
    //   title: t("common.opt"),
    //   fixed: "right",
    //   hidden: hiddenColumns.includes('id'),
    //   render: (_, item) =>
    //       <Button
    //         type="link"
    //         onClick={() => {
    //           setIsOpenEdit(true);
    //           setEditRow(item);
    //         }}
    //       >
    //         {t("product.modify")}
    //       </Button>
        
    // },
  ];

  const fetchProducts = (offset: number, filters: QuotationProductFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

    fetchList({ categoryId, params: {
      body: filters.keyword,
      pagination: { offset, take },
    } });
  };

  const exportColumns = (t: any) => [
    { key: 'product.code', label: t('product.code') },
    { key: 'product.name', label: t('product.name') },
    { key: 'product.brand1', label: t('product.brand1') },
    { key: 'product.brand2', label: t('product.brand2') },
    { key: 'product.uom', label: t('product.uom') },
    { key: 'category.hsCode', label: t('category.hsCode') },
    { key: 'product.cbm', label: `cbm(${t('product.cbm')})` },
    { key: 'product.process', label: `Process(${t('product.process')})` },
    { key: 'product.category', label: t('product.category') },
    { key: 'common.desc', label: t('common.desc') },
  ];

  const defaultSelectedColumns = (t: any) => [
    'product.code',
    'product.name',
    'product.brand1',
    'product.uom',
  ];

  const exportDataMapper = (item: Product, t: any) => {
    return {
      [t('product.code')]: item?.code || '',
      [t('product.name')]: item?.name || '',
      [t('product.brand1')]: item?.model || '',
      [t('product.brand2')]: item?.t1 || '',
      [t('product.uom')]: item?.uom || '',
      [t('category.hsCode')]: item?.hsCode || '',
      [`cbm(${t('product.cbm')})`]: item?.cbm || '',
      [`Process(${t('product.process')})`]: item?.process || '',
      [t('product.category')]: item?.category?.name || '',
      [t('common.desc')]: item?.desc || '',
    };
  };

  const handleExportAll = async (): Promise<Product[]> => {
    setExportLoading(true);
    setExportProgress(0);
    const allDataList: Product[] = [];
    try {
      const batchSize = 100;
      const firstRes = await categroyList({
        categoryId,
        params: {
          body: appliedFilters.keyword,
          pagination: { offset: 0, take: batchSize },
        },
      });

      if (firstRes?.body) {
        allDataList.push(...firstRes.body);
      }

      const totalSize = firstRes?.pagination?.size ?? 0;
      const batches = Math.ceil(totalSize / batchSize);

      for (let i = 1; i < batches; i++) {
        const offset = i * batchSize;
        const res = await categroyList({
          categoryId,
          params: {
            body: appliedFilters.keyword,
            pagination: { offset, take: batchSize },
          },
        });
        if (res?.body) {
          allDataList.push(...res.body);
        }
        setExportProgress(Math.round(((i + 1) / batches) * 100));
      }

      return allDataList;
    } catch (error) {
      console.error(t('common.exportErr'), error);
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetchProducts(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_QUOTATION_PRODUCT_FILTERS);
    fetchProducts(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex justify="space-between" align="center" style={{ width: "100%" }}>
      <Flex gap={10} align="center">
        <ExportExcelButton<Product>
          list={list}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="quotation_product_list.xlsx"
          sheetName={t('layout.gategoryManage')}
        />
        <ExportExcelButton<Product>
          list={list}
          isAll
          loadData={handleExportAll}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="quotation_product_list_all.xlsx"
          sheetName={t('layout.gategoryManage')}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <Tooltip>
              <Input
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                onPressEnter={submit}
                placeholder={t("order.searchPl")}
                style={{ width: 200 }}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const getTableLayout = () => {
    let props: ITableLayout = {
      loading,
      dataSource: list,
      columns: columns as ColumnsType<AnyObject>,
      headerChildren,
      tableLayoutRef: tableRef,
      total,
      handleChange: (offset: number) =>
        fetchProducts(offset, appliedFilters)
    }

    if (selectType === 'multi') {
      props = {
        ...props,
        rowKey: 'id',
        rowSelection: {
          type: "checkbox",
          selectedRowKeys: selectProIds,
          onChange: (val, n) => {
            flushSync(() => {
              setSelectProIds(() =>
                Array.from(new Set(val as Array<string>))
              );
              setSelectPros(() => _.uniqBy(n as Array<Product>, "id"));
            });
          },
          getCheckboxProps: (record) => ({
            name: record.id,
            // disabled: !!values.find((item) => item?.id === record.id),
          }),
        }
      }
    }

    const TableEle = <STableLayout {...props} />
    return TableEle
  }


  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      {getTableLayout()}
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {/* {(isOpenAdd || isOpenEdit) && (
        <AddOrEdit
          isAdd={isOpenAdd}
          categoryId={categoryId}
          openStatus={isOpenAdd || isOpenEdit}
          formData={isOpenAdd ? null : editRow}
          onOk={data => {
            if (isOpenAdd  && ((categoryId && (categoryId === data.categoryId)) || !categoryId)) {
              addItem(data)
            } else {
              if (categoryId && categoryId !== data.categoryId) {
                rmItem(data.id)
              } else {
                updateItem(data)
              }
            }
          }}
          onCancel={() => {
            if (isOpenAdd) {
              setIsOpenAdd(false);
            } else {
              setIsOpenEdit(false);
              setEditRow(null);
            }
          }}
        />
      )} */}
    </Flex>
  );
};

export default ProductList;
